import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAttachments } from '../resources/api.resource';
import { IAttachment } from '../models/Attachment';

export interface UseCollaborationAttachmentsOptions {
  collaborationId: string | undefined;
  enabled?: boolean;
}

export interface UseCollaborationAttachmentsResult {
  attachments: IAttachment[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useCollaborationAttachments({
  collaborationId,
  enabled = true,
}: UseCollaborationAttachmentsOptions): UseCollaborationAttachmentsResult {
  const query = useQuery({
    queryKey: ['collaborationAttachments', collaborationId],
    queryFn: async () => {
      if (!collaborationId) throw new Error('collaborationId is required');
      const attachments = await getAttachments(collaborationId);
      return (attachments || []) as IAttachment[];
    },
    enabled: enabled && !!collaborationId,
  });

  return {
    attachments: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}

export function invalidateAttachmentsCache(
  queryClient: ReturnType<typeof useQueryClient>,
  collaborationId: string
): void {
  queryClient.invalidateQueries({
    queryKey: ['collaborationAttachments', collaborationId],
  });
}
